import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { closeDetails, getCurrentPage, openDetails } from 'reducers/ui';
import styles from './index.module.scss';
import { HelpLinks } from 'utils/constants/helpLinks';
import { loginStatus } from 'utils/constants/status';
import staticText from 'static/text_display.json';
//emergency specific imports
import {
  emergencyDataGridAction,
  emergencySelectors,
  emergencyPageActions,
} from 'containers/Pages/Tasks/Emergencies';
import OperationalAction from 'components/Operational/operationalAction';
import API from 'services/api';
import { transformRaw } from 'utils/DataTransform/Personnel/PersonnelList';
import { serviceTypes } from 'utils/constants/serviceTypes.js';
import { PAGE_URLS } from 'utils/constants';
import isEmpty from 'lodash/isEmpty';
import BaseDetails from 'containers/Base/BaseDetails';
import { ManagerAndAbove, userHasRole } from 'utils/helper/roles';
import { getUserRoles } from 'reducers/users';
import { getEmergencyStatus } from '../Selectors/emergencySelectors';
import { translateValue } from 'utils/methods/helpers';

const allowedResponderStatus = [
  loginStatus.AVAILABLE,
  loginStatus.ON_INTERRUPTABLE_TASK,
];

const EmergencyDetails = ({ id, cancel }) => {
  //const classes = useStyles();
  const [selectedResponder, setSelectedResponder] = useState([]);
  //------------------------------Selectors---------------------------------
  const userRoles = useSelector(state => getUserRoles(state));
  const details = useSelector(state =>
    emergencySelectors.getEmergencyDetailTransformed(state),
  );
  const emergencyStatus = useSelector(state => getEmergencyStatus(state));
  const forceRefresh = useSelector(state =>
    emergencySelectors.getForceRefresh(state),
  );
  const pageUrl = useSelector(state => getCurrentPage(state));
  const selectedRowID = useSelector(state =>
    emergencySelectors.getSelectedRowID(state),
  );

  //-----------------------Actions------------------------------------------
  const printEmergencyDetails = id => {
    dispatchPrintEmergencyDetails(id);
  };

  const assignResponder = () => {
    if (selectedResponder) {
      dispatchAssignResponder({
        id: id,
        responder: selectedResponder.value,
      });
    }
  };
  const handleResolved = ({ resolvedAborted = true }) => {
    dispatchResolveEmergency(id, resolvedAborted);
    cancel();
    dispatch(closeDetails(pageUrl));
    if (resolvedAborted) {
      toggleAbortPopup();
    } else {
      toggleResolvePopup();
    }
  };
  const dispatch = useDispatch();

  const dispatchFetchEmergencyDetails = useCallback(
    id =>
      dispatch(
        emergencyDataGridAction.fetchGridRowDetails({
          id,
        }),
      ),
    [dispatch],
  );
  const dispatchOpenDetails = useCallback(url => dispatch(openDetails(url)), [
    dispatch,
  ]);

  const dispatchAssignResponder = useCallback(
    ({ id, responder }) => {
      dispatch(
        emergencyPageActions.assignResponder({
          id,
          responder,
        }),
      );
    },
    [dispatch],
  );
  const dispatchPrintEmergencyDetails = useCallback(
    id => {
      dispatch(
        emergencyPageActions.printEmergencyDetails({
          id,
        }),
      );
    },
    [dispatch],
  );
  const dispatchResolveEmergency = useCallback(
    (id, resolvedAborted) => {
      dispatch(
        emergencyPageActions.resolveEmergency({
          id,
          resolvedAborted,
        }),
      );
    },
    [dispatch],
  );
  //------------------------------------------------------------------------
  const getActionItem = type => {
    switch (type) {
      case 'assign-emergency':
        return {
          label: 'Assign Responder',
          placeholder: 'Select a responder',
          selectedValue: selectedResponder,
          changeSelectedValue: setSelectedResponder,
          isDisabled: false, //it is disabled when the status is resolved by hiding the whole panel
          btnText: 'Assign',
          btnSize: 's',
          btnClick: assignResponder,
          disabled: !selectedResponder,
          btnDisabled: !selectedResponder,
          fetchOptions: API.fetchUserList,
          dataTransform: ({ data }) => {
            return transformRaw(data);
          },
          fetchFilters: {
            status: allowedResponderStatus,
            service: [serviceTypes.emergency.standard],
            archived: false,
          },
          optionLabelKey: 'label',
        };
      default:
        return null;
    }
  };
  useEffect(() => {
    setSelectedResponder();
    if (!isEmpty(details) && details.others.responder) {
      setSelectedResponder({
        ...details.others.responder.raw,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details]);
  // NOTE: set in  hierarchical order. When we click the row of the emergency, we have access to the id field. When you are routed from some other page
  // back to emergency, we do not have access to id but details?.id?.value.
  // id needs to be put in first because when we click a new row, it will update the value of id to new uuid instantanously as selectedRowId and details?.id?.value
  // will take time to update as it comes from redux store.
  const detailID = id || selectedRowID || details?.others?.id?.value;
  useEffect(() => {
    if (detailID || forceRefresh) {
      dispatchFetchEmergencyDetails(detailID);
      dispatch(emergencyDataGridAction.setSelectedRowID(detailID));
      dispatchOpenDetails(PAGE_URLS.EMERGENCIES.link);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailID, forceRefresh]);

  const cancelButton = () => {
    // remove emergency detail panel from redux
    dispatch(closeDetails(PAGE_URLS.EMERGENCIES.link));
    dispatch(emergencyDataGridAction.setSelectedRowID(null));
    dispatch(
      emergencyDataGridAction.setGridRowDetails({
        details: {},
      }),
    );

    cancel();
  };
  const [showAbortPopup, setAbortPopup] = useState();
  const toggleAbortPopup = () => {
    setAbortPopup(!showAbortPopup);
  };
  const buttonAbortClick = () => {
    toggleAbortPopup();
  };
  const [showResolvePopup, setResolvePopup] = useState();
  const toggleResolvePopup = () => {
    setResolvePopup(!showResolvePopup);
  };
  const buttonResolveClick = () => {
    toggleResolvePopup();
  };
  const emergencyOperationalComponent = () => {
    return (
      <div className={styles.operationalAction}>
        <OperationalAction
          type={'asyncDropdown'}
          item={getActionItem('assign-emergency')}
        />
        <div className={styles.buttonContainer}>
          <OperationalAction
            type="button"
            item={{
              btnText: 'Abort',
              btnClickHandler: buttonAbortClick,
              outline: true,
            }}
            confirmPopup={{
              showConfirmPopup: showAbortPopup,
              popupTitle: translateValue('Abort Emergency'),
              popupStatusMessage: '',
              popupActionMessage: 'Do you wish to abort the emergency?',
              confirmBtnHandler: () =>
                handleResolved({ resolvedAborted: true }),
              confirmBtnText: translateValue(staticText.general.ok),
              cancelBtnHandler: toggleAbortPopup,
              cancelBtnText: translateValue(staticText.general.cancel),
              closePopupHandler: toggleAbortPopup,
            }}
          />
          <OperationalAction
            type="button"
            item={{
              btnText: 'Resolve',
              btnClickHandler: buttonResolveClick,
            }}
            confirmPopup={{
              showConfirmPopup: showResolvePopup,
              popupTitle: translateValue('Resolve Emergency'),
              popupStatusMessage: '',
              popupActionMessage: 'Do you wish to resolve the emergency?',
              confirmBtnHandler: () =>
                handleResolved({ resolvedAborted: false }),
              confirmBtnText: translateValue(staticText.general.ok),
              cancelBtnHandler: toggleResolvePopup,
              cancelBtnText: translateValue(staticText.general.cancel),
              closePopupHandler: toggleResolvePopup,
            }}
          />
        </div>
      </div>
    );
  };
  return (
    <BaseDetails
      header="Emergency Response"
      cancel={cancelButton}
      editable={false}
      downloadable={userHasRole({
        userRoles: userRoles,
        requiredRoles: ManagerAndAbove,
      })}
      onDownloadClick={() => printEmergencyDetails(details?.others?.id?.value)}
      formInfo={{
        link: HelpLinks.emergencyLink,
        value: HelpLinks.emergencyText,
      }}
      details={details}
      {...(emergencyStatus && {
        operationComponent: emergencyOperationalComponent(),
      })}
    />
  );
};
EmergencyDetails.propTypes = {
  cancel: PropTypes.func,

  id: PropTypes.string,
};

export default EmergencyDetails;
